import '@xyflow/react/dist/style.css';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  type Node,
  type NodeTypes,
  type ReactFlowInstance,
} from '@xyflow/react';
import { useAllSubjects } from '../hooks/use-subjects';
import { useSubjectEdges } from '../hooks/use-subject-edges';
import { useSubjectView } from '../hooks/use-subject-views';
import {
  buildSubjectGraph,
  type SubjectNodeData,
} from '../subject-graph-layout';
import {
  emphasizeNodes,
  filterByRoots,
  minZoomToFit,
  NEAR_ZOOM,
  plural,
  searchFocus,
  subjectColor,
  type SearchFocus,
} from '../subject-graph-view';
import { SubjectNode } from '../components/SubjectNode';
import { getApiErrorMessage } from '../display';
import {
  Button,
  ErrorState,
  Input,
  PageContainer,
} from '@react-apps/design-system';

// Estável entre renders (React Flow avisa se mudar de identidade a cada render).
const nodeTypes: NodeTypes = { subject: SubjectNode };

const CANVAS_HEIGHT = 'calc(100vh - 15rem)';

type SubjectFlow = ReactFlowInstance<Node<SubjectNodeData>>;

/** Detecta o dark mode (classe `.dark` no <html>) e reage a mudanças de tema. */
function useIsDark(): boolean {
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark'),
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

/** A linha que diz em texto o que a busca achou — o destaque não é só visual. */
function searchStatus(
  searched: string,
  focus: SearchFocus | null,
  nodes: Node<SubjectNodeData>[],
  viewActive: boolean,
  inHiddenRoot: boolean,
): string {
  if (!searched.trim()) return '';
  const center = focus && nodes.find((n) => n.id === focus.centerId);
  if (!focus || !center) {
    return inHiddenRoot
      ? 'Esse assunto está numa raiz desligada.'
      : 'Nenhum assunto com esse nome.';
  }
  const around = viewActive
    ? '.'
    : `; ${plural(focus.ancestorCount, 'ancestral', 'ancestrais')} e ${plural(focus.descendantCount, 'descendente', 'descendentes')} destacados.`;
  const homonyms =
    focus.matchIds.length > 1
      ? ` ${focus.matchIds.length} assuntos com esse nome.`
      : '';
  return `Centralizado em ${center.data.label}${around}${homonyms}`;
}

/**
 * Mapa de assuntos: o acervo inteiro, em árvore da esquerda para a direita, com nível de
 * detalhe por zoom e só o que está na tela desenhado. A busca centraliza num
 * assunto e destaca o entorno dele. Os botões das raízes, acima do mapa, ligam e
 * desligam cada raiz com tudo o que está abaixo dela.
 *
 * Plano: primum-core-root/docs/plans/in-review/mapa-de-assuntos-legivel.md
 */
export function SubjectGraphPage() {
  const isDark = useIsDark();
  const [searchParams] = useSearchParams();
  const focusViewId = searchParams.get('view') ?? undefined;

  const subjectsQuery = useAllSubjects();
  const edgesQuery = useSubjectEdges();
  const viewQuery = useSubjectView(focusViewId);

  const [flow, setFlow] = useState<SubjectFlow | null>(null);
  const [query, setQuery] = useState('');
  // O nome buscado, não o resultado: o foco se refaz quando o mapa recarrega
  // (depois de uma edição em outra aba, por exemplo) em vez de ficar velho.
  const [searched, setSearched] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  // Guarda as desligadas, não as ligadas: raiz nova que chega na recarga já
  // vem ligada, como as outras.
  const [hiddenRoots, setHiddenRoots] = useState<Set<string>>(new Set());
  const [showIsolated, setShowIsolated] = useState(true);

  const full = useMemo(
    () =>
      buildSubjectGraph(subjectsQuery.data?.items ?? [], edgesQuery.data ?? []),
    [subjectsQuery.data, edgesQuery.data],
  );

  const layout = useMemo(
    () =>
      filterByRoots(
        full,
        subjectsQuery.data?.items ?? [],
        edgesQuery.data ?? [],
        hiddenRoots,
        showIsolated,
      ),
    [full, subjectsQuery.data, edgesQuery.data, hiddenRoots, showIsolated],
  );

  // Ligar ou desligar refaz o layout: reenquadra no que ficou. Só nesse gesto,
  // não a cada recarga do acervo, que tiraria a câmera de onde o admin está.
  useEffect(() => {
    void flow?.fitView({ duration: 300 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenRoots, showIsolated]);

  function toggleRoot(id: string) {
    setHiddenRoots((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  // Foco por visão: só quando há membros resolvidos, para não escurecer tudo
  // durante loading/erro.
  const viewMemberIds = useMemo(() => {
    const ids = new Set(
      (viewQuery.data?.members ?? []).map((m) => m.subjectId),
    );
    return focusViewId && ids.size > 0 ? ids : null;
  }, [focusViewId, viewQuery.data]);

  const focus = useMemo(
    () => searchFocus(searched, layout.nodes, layout.edges, !!viewMemberIds),
    [searched, layout, viewMemberIds],
  );

  const nodes = useMemo(
    () => emphasizeNodes(layout.nodes, viewMemberIds, focus, isDark),
    [layout.nodes, viewMemberIds, focus, isDark],
  );

  const names = useMemo(
    () => [...new Set(layout.nodes.map((n) => n.data.label))].sort(),
    [layout.nodes],
  );

  // Do acervo inteiro: com raízes desligadas o mapa só encolhe, e o zoom
  // mínimo não precisa mudar a cada clique.
  const minZoom = useMemo(() => minZoomToFit(full.nodes), [full.nodes]);

  function clearSearch() {
    setQuery('');
    setSearched('');
    searchRef.current?.focus();
  }

  function onSearch(event: FormEvent) {
    event.preventDefault();
    setSearched(query);
    const found = searchFocus(
      query,
      layout.nodes,
      layout.edges,
      !!viewMemberIds,
    );
    const center = found && layout.nodes.find((n) => n.id === found.centerId);
    if (!center) return;
    void flow?.setCenter(
      center.position.x + (center.width ?? 0) / 2,
      center.position.y + (center.height ?? 0) / 2,
      { zoom: Math.max(NEAR_ZOOM, 1), duration: 400 },
    );
  }

  const status = searchStatus(
    searched,
    focus,
    layout.nodes,
    !!viewMemberIds,
    !focus && !!searchFocus(searched, full.nodes, [], false),
  );

  const isLoading = subjectsQuery.isLoading || edgesQuery.isLoading;
  const error = subjectsQuery.error ?? edgesQuery.error;
  const total = subjectsQuery.data?.total ?? 0;
  const edgeCount = (edgesQuery.data ?? []).length;

  return (
    <PageContainer maxWidth="6xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/content/edges"
            className="text-(--color-text-secondary) hover:text-(--color-text-primary) text-sm"
          >
            ← Voltar para Conteúdo
          </Link>
          <h1 className="text-(--color-text-primary) mt-1 text-2xl font-semibold">
            Mapa de assuntos
          </h1>
          <p className="text-(--color-text-secondary) mt-1 text-sm">
            Árvore dos assuntos, das raízes (à esquerda) às folhas (à direita).
            Arraste o fundo para navegar, use a roda para aproximar — de perto
            aparecem nome, papel e contagens — e busque um assunto para ver o
            entorno dele.
          </p>
        </div>
        {!isLoading && !error && (
          <span className="text-(--color-text-secondary) text-sm">
            {total} assunto{total === 1 ? '' : 's'} · {edgeCount} relaç
            {edgeCount === 1 ? 'ão' : 'ões'} · {full.isolatedCount} solto
            {full.isolatedCount === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {focusViewId && viewQuery.data && (
        <div className="border-(--color-accent)/40 bg-(--color-accent)/10 flex flex-wrap items-center justify-between gap-2 rounded-lg border px-4 py-2">
          <span className="text-(--color-text-primary) text-sm">
            Destacando a visão{' '}
            <strong className="font-semibold">
              {viewQuery.data.view.name}
            </strong>{' '}
            <span className="text-(--color-text-secondary)">
              ({viewMemberIds?.size ?? 0} assunto
              {viewMemberIds?.size === 1 ? '' : 's'})
            </span>
          </span>
          <Link
            to="/content/graph"
            className="text-(--color-accent) text-sm font-medium hover:underline"
          >
            Limpar foco
          </Link>
        </div>
      )}

      {error ? (
        <ErrorState
          message={getApiErrorMessage(error, 'Falha ao carregar o mapa.')}
          onRetry={() => {
            void subjectsQuery.refetch();
            void edgesQuery.refetch();
          }}
        />
      ) : isLoading ? (
        <div
          role="status"
          className="border-(--color-border) bg-(--color-bg-secondary) flex animate-pulse items-center justify-center rounded-xl border"
          style={{ height: CANVAS_HEIGHT }}
        >
          <span className="text-(--color-text-secondary) text-sm">
            Carregando mapa…
          </span>
        </div>
      ) : full.nodes.length === 0 ? (
        <div className="border-(--color-border) bg-(--color-bg-primary) rounded-xl border p-8 text-center">
          <p className="text-(--color-text-secondary) text-sm">
            Nenhum assunto cadastrado ainda.{' '}
            <Link
              to="/content/edges"
              className="text-(--color-accent) font-medium hover:underline"
            >
              Cadastre assuntos e relações
            </Link>
            .
          </p>
        </div>
      ) : (
        <>
          <form
            onSubmit={onSearch}
            className="flex flex-wrap items-end gap-2"
            role="search"
          >
            <div className="min-w-64 flex-1">
              <Input
                ref={searchRef}
                id="subject-graph-search"
                label="Buscar assunto"
                list="subject-graph-names"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') clearSearch();
                }}
                autoComplete="off"
              />
              {/* ponytail: <datalist> nativo com mil opções não segue o tema e
                  é fraco no Safari/mobile; trocar por combobox quando o
                  design-system tiver um. */}
              <datalist id="subject-graph-names">
                {names.map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            </div>
            <Button type="submit" size="md">
              Buscar
            </Button>
            {focus && (
              <Button type="button" variant="secondary" onClick={clearSearch}>
                Limpar
              </Button>
            )}
          </form>
          <p
            role="status"
            aria-live="polite"
            className="text-(--color-text-secondary) min-h-5 text-sm"
          >
            {status}
          </p>

          {/* Desligada: amostra vazada e nome riscado, não só a cor mais fraca. */}
          <div
            role="group"
            aria-label="Raízes no mapa"
            className="flex max-h-24 flex-wrap gap-2 overflow-y-auto p-0.5"
          >
            {[
              ...full.roots.map((root, i) => ({
                id: root.id,
                name: root.name,
                color: subjectColor(i, isDark),
                on: !hiddenRoots.has(root.id),
                toggle: () => toggleRoot(root.id),
              })),
              ...(full.isolatedCount > 0
                ? [
                    {
                      id: 'soltos',
                      name: 'Soltos',
                      color: subjectColor(null, isDark),
                      on: showIsolated,
                      toggle: () => setShowIsolated((v) => !v),
                    },
                  ]
                : []),
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                aria-pressed={t.on}
                onClick={t.toggle}
                className={[
                  'border-(--color-border) focus-visible:ring-(--color-accent) inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2',
                  t.on
                    ? 'bg-(--color-bg-secondary) text-(--color-text-primary)'
                    : 'text-(--color-text-secondary) line-through',
                ].join(' ')}
              >
                <span
                  aria-hidden
                  className="inline-block h-2.5 w-2.5 rounded-full border-2"
                  style={{
                    borderColor: t.color,
                    background: t.on ? t.color : 'transparent',
                  }}
                />
                {t.name}
              </button>
            ))}
          </div>

          <div
            className="border-(--color-border) overflow-hidden rounded-xl border"
            style={{ height: CANVAS_HEIGHT }}
          >
            {layout.nodes.length === 0 ? (
              <p className="text-(--color-text-secondary) flex h-full items-center justify-center text-sm">
                Tudo desligado. Ligue uma raiz acima para ver o mapa.
              </p>
            ) : (
              <ReactFlow
                aria-label="Mapa de assuntos"
                nodes={nodes}
                edges={layout.edges}
                nodeTypes={nodeTypes}
                onInit={setFlow}
                colorMode={isDark ? 'dark' : 'light'}
                onlyRenderVisibleElements
                nodesDraggable={false}
                nodesConnectable={false}
                nodesFocusable={false}
                edgesFocusable={false}
                elementsSelectable={false}
                fitView
                minZoom={minZoom}
              >
                <Background />
                <Controls showInteractive={false} />
                <MiniMap<Node<SubjectNodeData>>
                  pannable
                  zoomable
                  nodeColor={(n) => n.data.color ?? ''}
                />
              </ReactFlow>
            )}
          </div>

          <section
            aria-label="Legenda"
            className="text-(--color-text-secondary) flex flex-col gap-2 text-xs"
          >
            <ul className="flex flex-wrap gap-x-4 gap-y-1">
              <li>
                <span
                  aria-hidden
                  className="outline-(--color-text-primary) mr-1 inline-block h-3 w-4 rounded-sm align-middle outline-4"
                />
                raiz: contorno grosso, na primeira coluna
              </li>
              <li>
                <span
                  aria-hidden
                  className="border-(--color-text-secondary) mr-1 inline-block h-3 w-4 rounded-full border align-middle"
                />
                folha: pílula, sem filhos, na ponta da direita
              </li>
              <li>
                <span
                  aria-hidden
                  className="border-(--color-text-secondary) mr-1 inline-block h-3 w-4 border align-middle"
                />
                solto: cantos retos, sem pai nem filho, no bloco à direita
              </li>
              <li>
                <svg
                  aria-hidden
                  width="24"
                  height="8"
                  className="mr-1 inline-block align-middle"
                >
                  <line
                    x1="0"
                    y1="4"
                    x2="24"
                    y2="4"
                    stroke="currentColor"
                    strokeDasharray="6 4"
                  />
                </svg>
                outro pai (o assunto fica junto do pai mais perto da raiz)
              </li>
            </ul>
          </section>
        </>
      )}
    </PageContainer>
  );
}
